import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { DashboardStats } from '../types';
import DoughnutChart from '../components/DoughnutChart';
import { toTitleCase } from '../utils/formatName';

const DashboardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [statsError, setStatsError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiService
      .getDashboardStats()
      .then(setStats)
      .catch((err) => setStatsError(err.message || 'Failed to load dashboard stats'))
      .finally(() => setLoading(false));
  }, []);

  const statCards = [
    { label: 'Total Scholars', value: stats?.totalScholars ?? 0, icon: '👥', color: '#8B5CF6' },
    { label: 'Pending Applications', value: stats?.pendingApplications ?? 0, icon: '⏳', color: '#EC4899' },
    { label: 'Approved', value: stats?.approvedApplications ?? 0, icon: '✅', color: '#10B981' },
    { label: 'Renewals Due', value: stats?.renewalsDue ?? 0, icon: '🔄', color: '#F59E0B' }
  ];

  // Both category breakdowns are shown as a doughnut only — its legend
  // already lists every label with its count, so a row of stat cards
  // above it was the same four numbers twice.
  const scholarCategorySlices = [
    { label: 'Senior High Scholars', value: stats?.seniorHighScholars ?? 0, color: '#3B82F6' },
    { label: 'College Scholars', value: stats?.collegeScholars ?? 0, color: '#8B5CF6' },
    { label: 'Special Course Scholars', value: stats?.specialCourseScholars ?? 0, color: '#EC4899' },
    { label: 'ALS Scholars', value: stats?.alsScholars ?? 0, color: '#10B981' }
  ];

  // Same four groups as the scholar slices, so the two charts read
  // together. These won't always sum to Pending Applications above — an
  // applicant who hasn't picked a year level yet counts in the total but
  // in none of the categories, which the caption below spells out.
  const pendingCategorySlices = [
    { label: 'Senior High Pending', value: stats?.pendingSeniorHighApplications ?? 0, color: '#3B82F6' },
    { label: 'College Pending', value: stats?.pendingCollegeApplications ?? 0, color: '#8B5CF6' },
    { label: 'Special Course Pending', value: stats?.pendingSpecialCourseApplications ?? 0, color: '#EC4899' },
    { label: 'ALS Pending', value: stats?.pendingAlsApplications ?? 0, color: '#10B981' }
  ];

  const categorizedPendingTotal = pendingCategorySlices.reduce((sum, slice) => sum + slice.value, 0);
  const uncategorizedPending = (stats?.pendingApplications ?? 0) - categorizedPendingTotal;

  return (
    <div>
      <nav className="navbar">
        <div className="navbar-brand">Dashboard</div>
        <div className="navbar-actions">
          <div className="user-menu">
            <span>👤</span>
            <span>{user ? `${user.firstName} ${user.lastName}` : ''}</span>
          </div>
        </div>
      </nav>

      <div className="container">
        <div className="page-header">
          <h1>Welcome back, {toTitleCase(user?.firstName)}</h1>
          <p>Here's what's happening with your scholarships today</p>
        </div>

        {statsError && (
          <div className="alert-error" style={{ padding: '1rem', marginBottom: '1.5rem' }}>
            {statsError}
          </div>
        )}

        {loading ? (
          <p>Loading...</p>
        ) : (
          <>
            {/* Key Metrics */}
            <section style={{ marginBottom: '3rem' }}>
              <div className="stats">
                {statCards.map((item) => (
                  <div className="stat" key={item.label}>
                    <div className="stat-icon" style={{ background: `linear-gradient(135deg, ${item.color}, ${item.color}BB)` }}>
                      {item.icon}
                    </div>
                    <h3>{item.value.toLocaleString()}</h3>
                    <p>{item.label}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Pending Applications by Category */}
            <section style={{ marginBottom: '3rem' }}>
              <div className="card-header" style={{ marginBottom: '1rem' }}>
                <h3>Pending Applications by Category</h3>
              </div>
              {uncategorizedPending > 0 && (
                <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', margin: '0 0 1rem 0' }}>
                  {uncategorizedPending.toLocaleString()} pending{' '}
                  {uncategorizedPending === 1 ? 'application has' : 'applications have'} no year level set yet, so
                  {uncategorizedPending === 1 ? ' it is' : ' they are'} not counted in the chart below.
                </p>
              )}
              <div className="card">
                <DoughnutChart
                  data={pendingCategorySlices}
                />
              </div>
            </section>

            {/* Scholar by Category */}
            <section style={{ marginBottom: '3rem' }}>
              <div className="card-header" style={{ marginBottom: '1rem' }}>
                <h3>Scholar by Category</h3>
              </div>
              <div className="card">
                <DoughnutChart
                  data={scholarCategorySlices}
                />
              </div>
            </section>
          </>
        )}

        {/* Quick Actions */}
        <section>
          <div className="card">
            <div className="card-header">
              <h3>Quick Actions</h3>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <button
                onClick={() => navigate('/applications')}
                style={{
                  padding: '1rem',
                  background: 'rgba(139, 92, 246, 0.05)',
                  border: '2px solid rgba(139, 92, 246, 0.2)',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  fontWeight: 500,
                  color: 'var(--text-primary)',
                  textAlign: 'left',
                  transition: 'all 0.3s ease'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <span style={{ fontSize: '1.25rem' }}>📝</span>
                  <span>Review Applications</span>
                </div>
                <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', margin: '0.5rem 0 0 0' }}>
                  Applications awaiting a decision
                </p>
              </button>
              <button
                onClick={() => navigate('/scholars')}
                style={{
                  padding: '1rem',
                  background: 'rgba(236, 72, 153, 0.05)',
                  border: '2px solid rgba(236, 72, 153, 0.2)',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  fontWeight: 500,
                  color: 'var(--text-primary)',
                  textAlign: 'left',
                  transition: 'all 0.3s ease'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <span style={{ fontSize: '1.25rem' }}>👥</span>
                  <span>Manage Scholars & Renewals</span>
                </div>
                <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', margin: '0.5rem 0 0 0' }}>
                  Grades, allowances, and pending renewal requests
                </p>
              </button>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default DashboardPage;
